import React from "react";
import { useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import { IRepoV2ReduxState } from "repoV2/features/Common/modules/Reducer/Reducer.interface";
import { LISTING_TYPES } from "repoV2/features/Listing/Listing.constants";
import { getCurrencyLocaleValue } from "repoV2/features/Common/modules/Location/utils/getLocales";
import { formatNumberWithCommas } from "repoV2/features/Common/utils/numbers";
import CheckoutButton from "../CheckoutButton/CheckoutButton";
import { IPriceDetailsSection } from "../../interfaces/CheckoutPages.interface";
import { useCheckoutBasicDetailsContext } from "../CheckoutBasicDetailsContextProvider";
import { ICheckoutSubmitButton } from "./CheckoutSubmitButton.interfaces";
import { useGetPriceSummary } from "../PaymentSummarySection/utils/priceSummaryReducer";
import { BillItemShimmer } from "../PaymentSummarySection/modules/BillItemShimmer/BillItemShimmer";
import useGetIsPaymentProcessing from "../../utils/useGetIsPaymentProcessing";
import styles from "./CheckoutSubmitButton.module.scss";
import { checkout_page_button_types } from "../../constants/CheckoutPages.constants";
import useMerchandiseHelper from "../PaymentDetailsSection/modules/FormGroups/modules/MerchandiseDetailsFormGroup/utils/useMerchandiseHelper";

// Common component for checkout submit button
const CheckoutSubmitButton = (props: ICheckoutSubmitButton) => {
    const { type, onClick } = props;
    const { totalAmount, strikeOffAmount } = useGetPriceSummary();
    const {
        listingUuid,
        listingType,
        checkoutPageButtonType,
        checkoutPageButtonText: { paymentButtonText },
    } = useCheckoutBasicDetailsContext();

    const { isPaymentProcessing } = useGetIsPaymentProcessing();

    const {
        isVariantMerchandise,
        selectedMerchandiseVariant,
        shiprocketData,
        isShiprocketEnabled,
    } = useMerchandiseHelper();

    let hideMerchandisePriceValue = false;
    if (listingType === LISTING_TYPES.MERCHANDISE) {
        if (
            isVariantMerchandise &&
            !Object.keys(selectedMerchandiseVariant).length
        ) {
            hideMerchandisePriceValue = true;
        }
        if (
            isShiprocketEnabled &&
            (!shiprocketData.isVerified || shiprocketData.error)
        ) {
            hideMerchandisePriceValue = true;
        }
    }

    const fileUploadingLoadingState =
        useSelector(
            (state: IRepoV2ReduxState) =>
                state.checkoutPages.listingDetails?.[listingUuid]
                    ?.fileUploadingDetails
        ) || {};

    // Need to check incase any of the questions has a file being uploading  to them
    const isFileUploadProcessing = Object.values(
        fileUploadingLoadingState
    ).some(loading => loading);

    const priceDetailsSection: IPriceDetailsSection = useSelector(
        (state: IRepoV2ReduxState) =>
            state.checkoutPages.listingDetails[listingUuid]?.priceSection
    );

    const priceDetails = priceDetailsSection?.data;
    const priceDetailsIsFetched = priceDetailsSection?.isFetched;

    const sessionDetailsIsFetched = useSelector(
        (state: IRepoV2ReduxState) =>
            state.checkoutPages.listingDetails[listingUuid]?.sessionDetails
                ?.isFetched
    );

    const isFetched = sessionDetailsIsFetched && priceDetailsIsFetched;

    const currency = priceDetails?.currency || "";
    const currencyName = priceDetails?.currency_name || "";
    const currencyLocale = getCurrencyLocaleValue(currencyName);

    const isCheckoutCustomButton =
        checkoutPageButtonType === checkout_page_button_types.custom;

    const showFreePriceText = isFetched
        ? +totalAmount === 0 && totalAmount === strikeOffAmount
        : false;

    // incase we have to hide the merchandise price value, and it is not a custom button
    const isMerchandiseWPriceNotCalculated =
        hideMerchandisePriceValue && !isCheckoutCustomButton;

    // If payment is not processing, and we have to render custom checkout button tex
    const showCustomButtonText = !isPaymentProcessing && isCheckoutCustomButton;

    // If payment is not processing and we do not have to show custom checkout button text,
    // and merchandise price is also not hidden.
    // and we have to show the free price text
    const showFreeListingText =
        !isPaymentProcessing &&
        !isCheckoutCustomButton &&
        showFreePriceText &&
        !hideMerchandisePriceValue;

    // If payment is not processing and we do not have to show custom checkout button
    // text, and we don't have to show free price text either,, and we don't have to hide
    // merchandise price either
    const showDefaultPaymentText =
        !isPaymentProcessing &&
        !isCheckoutCustomButton &&
        !showFreePriceText &&
        !hideMerchandisePriceValue;

    const defaultPaymentText = (
        <BillItemShimmer
            fetchedJSX={
                <>
                    Pay&nbsp;
                    {currency}
                    {formatNumberWithCommas({
                        num: totalAmount,
                        locale: currencyLocale,
                    })}
                    &nbsp;
                    {+strikeOffAmount > +totalAmount ? (
                        <div className={styles.strikeOffTextStyle}>
                            <del>
                                {currency}
                                {formatNumberWithCommas({
                                    num: strikeOffAmount,
                                    locale: currencyLocale,
                                })}
                            </del>
                            &nbsp;
                        </div>
                    ) : null}
                </>
            }
            isFetched={isFetched}
            overrideClassName={styles.amountShimmer}
        />
    );

    return (
        <CheckoutButton
            type={type || "submit"}
            onClick={onClick}
            className="d-flex align-items-center justify-content-center"
            disabled={isPaymentProcessing || isFileUploadProcessing}
        >
            {/* If we are still processing payment - user has clicked on pay button, then we show loader */}
            {isPaymentProcessing && (
                <>
                    Processing
                    <CircularProgress className={styles.circularProgress} />
                </>
            )}
            {isMerchandiseWPriceNotCalculated && <>Purchase</>}
            {showCustomButtonText ? paymentButtonText : <></>}
            {showFreeListingText ? <>Get it for FREE!</> : <></>}
            {showDefaultPaymentText ? defaultPaymentText : <></>}
        </CheckoutButton>
    );
};

export default CheckoutSubmitButton;
